// frontend/src/components/admin/LoginAttempts.jsx
// ============================================================================
// TENTATIVES DE CONNEXION
// ----------------------------------------------------------------------------
// Repond a une question precise : depuis qu'une entreprise est bloquee ou
// suspendue, a-t-elle essaye de se connecter, quand, et avec quel compte ?
//
// Les dates arrivent du serveur en UTC (texte ISO) et sont affichees a l'heure
// de Brazzaville, qui ne change pas avec les saisons.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert, RefreshCw, Search, Building2, Clock,
  CheckCircle2, XCircle, KeyRound, Ban, Globe, Monitor, Calendar, MapPin
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const FUSEAU = 'Africa/Brazzaville';

// Un ton par famille de resultat : vert pour ce qui passe, rouge pour ce qui
// est refuse par nous, ambre pour ce qui echoue cote utilisateur.
const TONS = {
  succes:             { libelle: 'Connexion réussie',    ton: 'succes',  Icon: CheckCircle2 },
  mot_de_passe:       { libelle: 'Mot de passe incorrect', ton: 'alerte', Icon: KeyRound },
  email_inconnu:      { libelle: 'Adresse inconnue',     ton: 'alerte',  Icon: Search },
  compte_inactif:     { libelle: 'Compte désactivé',     ton: 'refus',   Icon: Ban },
  entreprise_bloquee: { libelle: 'Entreprise bloquée',   ton: 'refus',   Icon: Ban },
  abonnement_suspendu:{ libelle: 'Abonnement suspendu',  ton: 'refus',   Icon: Ban },
  essai_expire:       { libelle: "Essai expiré",         ton: 'refus',   Icon: Clock },
  sans_entreprise:    { libelle: 'Compte sans entreprise', ton: 'refus', Icon: Building2 },
  hors_centre:        { libelle: 'Domaine refusé',       ton: 'refus',   Icon: Globe },
  totp_requis:        { libelle: 'Code demandé',         ton: 'neutre',  Icon: KeyRound },
  totp_invalide:      { libelle: 'Code invalide',        ton: 'alerte',  Icon: XCircle }
};

const FENETRES = [
  { valeur: 7,   libelle: '7 jours' },
  { valeur: 30,  libelle: '30 jours' },
  { valeur: 90,  libelle: '90 jours' },
  { valeur: 0,   libelle: 'Tout' }
];

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const quand = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
};

const ilYA = (iso) => {
  if (!iso) return '';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(minutes)) return '';
  if (minutes < 60) return `il y a ${Math.max(minutes, 1)} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  const jours = Math.round(heures / 24);
  return `il y a ${jours} j`;
};

// Mozilla/5.0 (Linux; Android 10; K) ... Chrome/152 Mobile Safari -> Chrome, Android
const appareil = (ua) => {
  if (!ua) return '—';
  const navigateur =
    /Edg\//.test(ua) ? 'Edge' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    /curl/i.test(ua) ? 'curl' : 'Navigateur';
  const systeme =
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad/.test(ua) ? 'iOS' :
    /Windows/.test(ua) ? 'Windows' :
    /Mac OS X/.test(ua) ? 'Mac' :
    /Linux/.test(ua) ? 'Linux' : '';
  return systeme ? `${navigateur} · ${systeme}` : navigateur;
};

// ---------------------------------------------------------------------------
// Briques
// ---------------------------------------------------------------------------

const Pastille = ({ resultat, t }) => {
  const cfg = TONS[resultat] || { libelle: resultat, ton: 'neutre', Icon: XCircle };
  const couleurs = {
    succes: { fond: t.successSoft, texte: t.successText },
    refus:  { fond: t.dangerSoft,  texte: t.dangerText },
    alerte: { fond: t.warningSoft, texte: t.warningText },
    neutre: { fond: t.bgInput,     texte: t.textSecondary }
  }[cfg.ton];
  const Icon = cfg.Icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      borderRadius: 99, fontSize: 12, fontWeight: 700,
      background: couleurs.fond, color: couleurs.texte, whiteSpace: 'nowrap' }}>
      <Icon size={12} />
      {cfg.libelle}
    </span>
  );
};

const Carte = ({ Icon, libelle, valeur, detail, couleur, t }) => (
  <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 42, height: 42, borderRadius: 12, background: `${couleur}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} style={{ color: couleur }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, margin: 0,
        textTransform: 'uppercase', letterSpacing: '0.5px' }}>{libelle}</p>
      <p style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: 0, lineHeight: 1.2 }}>{valeur}</p>
      {detail && <p style={{ fontSize: 12, color: t.textMuted, margin: '2px 0 0' }}>{detail}</p>}
    </div>
  </div>
);

const champStyle = (t) => ({
  background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
  padding: '9px 12px', fontSize: 13, color: t.text, outline: 'none', fontFamily: 'inherit'
});

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

const LoginAttempts = () => {
  const t = useTheme();

  const [tentatives, setTentatives] = useState([]);
  const [resume, setResume] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [entreprise, setEntreprise] = useState('');
  const [resultat, setResultat] = useState('echecs');
  const [fenetre, setFenetre] = useState(30);
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [journal, compteurs] = await Promise.all([
        superAdminApi.connexions.liste({
          company_id: entreprise,
          resultat,
          jours: fenetre,
          email: recherche.trim(),
          limit: 300
        }),
        superAdminApi.connexions.resume(fenetre)
      ]);
      setTentatives(journal.data || []);
      setResume(compteurs.data || []);
    } catch (e) {
      setErreur(e.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, [entreprise, resultat, fenetre, recherche]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    superAdminApi.companies.getAll()
      .then((r) => setEntreprises(r.data || []))
      .catch(() => setEntreprises([]));
  }, []);

  // Ce qui merite d'etre vu en premier : une entreprise coupee qui insiste.
  const insistantes = useMemo(
    () => resume.filter((r) => (r.is_blocked || r.status === 'suspended') && Number(r.echecs) > 0),
    [resume]
  );

  const totalEchecs = useMemo(
    () => resume.reduce((somme, r) => somme + Number(r.echecs || 0), 0),
    [resume]
  );
  const totalTentatives = useMemo(
    () => resume.reduce((somme, r) => somme + Number(r.tentatives || 0), 0),
    [resume]
  );

  const periode = FENETRES.find((f) => f.valeur === fenetre)?.libelle || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <ShieldAlert size={20} style={{ color: t.primary }} />
            Tentatives de connexion
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            Chaque passage par la page de connexion, abouti ou refusé. Heures de Brazzaville.
          </p>
        </div>
        <button
          type="button"
          onClick={charger}
          style={{ ...champStyle(t), display: 'inline-flex', alignItems: 'center', gap: 8,
            fontWeight: 600, cursor: 'pointer', height: 38 }}
        >
          <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      {/* Compteurs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Carte Icon={Calendar}   libelle="Tentatives" valeur={totalTentatives} detail={periode}
               couleur={t.primary} t={t} />
        <Carte Icon={XCircle}    libelle="Refusées"   valeur={totalEchecs} detail={periode}
               couleur={t.danger} t={t} />
        <Carte Icon={Building2}  libelle="Entreprises coupées qui insistent"
               valeur={insistantes.length} detail={insistantes.length ? 'voir ci-dessous' : 'aucune'}
               couleur={t.warning} t={t} />
      </div>

      {/* Entreprises coupées qui continuent d'essayer */}
      {insistantes.length > 0 && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {insistantes.map((r) => (
            <div key={r.company_id} style={{ display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Ban size={16} style={{ color: t.dangerText, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{r.company_name}</span>
                <span style={{ fontSize: 13, color: t.textSecondary }}>
                  {r.is_blocked ? 'bloquée' : 'suspendue'} · {r.echecs} tentative{Number(r.echecs) > 1 ? 's' : ''} refusée{Number(r.echecs) > 1 ? 's' : ''}
                  {Number(r.succes) > 0 ? ` · ${r.succes} connexion(s) aboutie(s)` : ''}
                  {r.pays ? ` · depuis : ${r.pays}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, color: t.textMuted }}>
                  dernier essai {quand(r.derniere_tentative)} ({ilYA(r.derniere_tentative)})
                </span>
                <button
                  type="button"
                  onClick={() => { setEntreprise(String(r.company_id)); setResultat('echecs'); }}
                  style={{ ...champStyle(t), padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', background: t.bgCard }}
                >
                  Voir le détail
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
        padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Entreprise</span>
          <select value={entreprise} onChange={(e) => setEntreprise(e.target.value)} style={champStyle(t)}>
            <option value="">Toutes</option>
            {entreprises.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Résultat</span>
          <select value={resultat} onChange={(e) => setResultat(e.target.value)} style={champStyle(t)}>
            <option value="echecs">Refusées seulement</option>
            <option value="">Toutes</option>
            <option value="succes">Connexions réussies</option>
            <option value="entreprise_bloquee">Entreprise bloquée</option>
            <option value="abonnement_suspendu">Abonnement suspendu</option>
            <option value="mot_de_passe">Mot de passe incorrect</option>
            <option value="email_inconnu">Adresse inconnue</option>
            <option value="totp_invalide">Code invalide</option>
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Période</span>
          <select value={fenetre} onChange={(e) => setFenetre(Number(e.target.value))} style={champStyle(t)}>
            {FENETRES.map((f) => (
              <option key={f.valeur} value={f.valeur}>{f.libelle}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexGrow: 1, minWidth: 220 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Compte</span>
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="une partie de l'adresse e-mail"
            style={{ ...champStyle(t), flexGrow: 1 }}
          />
        </label>
      </div>

      {/* Journal */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
        overflow: 'hidden' }}>
        {erreur && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.dangerText }}>{erreur}</p>
        )}

        {!erreur && chargement && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>Lecture du journal…</p>
        )}

        {!erreur && !chargement && tentatives.length === 0 && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>
            Aucune tentative sur cette période avec ces filtres.
          </p>
        )}

        {!erreur && !chargement && tentatives.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: t.bgTableHeader }}>
                {['Date et heure', 'Compte', 'Entreprise', 'Résultat', 'Provenance', 'Appareil'].map((titre) => (
                  <th key={titre} style={{ textAlign: 'left', padding: '11px 16px', fontSize: 11,
                    fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: `1px solid ${t.border}` }}>{titre}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tentatives.map((ligne) => (
                <tr key={ligne.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '11px 16px', fontSize: 13, color: t.text, whiteSpace: 'nowrap' }}>
                    {quand(ligne.created_at)}
                    <span style={{ color: t.textMuted, marginLeft: 8, fontSize: 12 }}>{ilYA(ligne.created_at)}</span>
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 13, color: t.text }}>
                    {ligne.email}
                    {ligne.role && (
                      <span style={{ color: t.textMuted, marginLeft: 8, fontSize: 12 }}>{ligne.role}</span>
                    )}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                    {ligne.company_name || '—'}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <Pastille resultat={ligne.resultat} t={t} />
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                    {ligne.pays ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <MapPin size={13} style={{ color: t.textMuted, flexShrink: 0 }} />
                        <span>
                          <span style={{ color: t.text, fontWeight: 600 }}>{ligne.pays}</span>
                          {ligne.operateur && (
                            <span style={{ color: t.textMuted, fontSize: 12 }}> · {ligne.operateur}</span>
                          )}
                          <br />
                          <span style={{ fontSize: 12, color: t.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {ligne.ip || '—'}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ligne.ip || '—'}</span>
                    )}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Monitor size={13} style={{ color: t.textMuted }} />
                      {appareil(ligne.user_agent)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        Les tentatives antérieures au 24 septembre 2026 ont été reprises du journal du serveur ;
        leur adresse IP n&rsquo;est connue que sur les deux dernières semaines.<br />
        La provenance vient d&rsquo;une base locale mise à jour chaque mois : le pays est fiable,
        l&rsquo;opérateur est celui à qui la plage est enregistrée, et une adresse mobile désigne
        un réseau — jamais une personne. Derrière un VPN, c&rsquo;est le VPN qui s&rsquo;affiche.
      </p>
    </div>
  );
};

export default LoginAttempts;
